import { css, cx } from 'linaria';
import React, { forwardRef } from 'react';
import Box from '../box';
import { useFormControl } from '../form-control';
import { default as IconAlert } from '../../../icons/16px/alert.svg';
import { useId } from '../hooks/use-id';
import useInputStyles from './input.styles';

type InputElements = 'input' | 'textarea';
export type InputVariants = 'primary';
export type InputSizes = 'small' | 'base' | 'medium';

export interface InputProps extends Omit<React.AllHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, 'size'> {
    /**
     * Set to 'textarea' to render as multiline.
     */
    as?: InputElements;
    /**
     * Input size.
     */
    size?: InputSizes;
    variant?: InputVariants;
    /**
     * Input will use 100% width.
     */
    fullWidth?: boolean;
    /**
     * The input has an error state.
     */
    error?: boolean;
    /**
     * Disable the native browser resizing for multiline (textarea) inputs.
     */
    noResize?: boolean;

    /**
     * Icon displayed at left of input.
     */
    startAdornmentSlot?: React.ReactElement;
    /**
     * Icon displayed at right of input.
     */
    endAdornmentSlot?: React.ReactElement;

    /**
     * Function to return the content length.
     */
    lengthFn?: (val: string) => number;
}

const InputComponent = (props: InputProps, ref: React.Ref<HTMLInputElement | HTMLTextAreaElement>): JSX.Element => {
    const formControl = useFormControl();
    const defaultId = useId();

    const {
        as = 'input',
        size = 'base',
        variant = 'primary',
        'aria-label': ariaLabel,
        fullWidth = formControl?.fullWidth,
        onChange,
        error = formControl?.hasError,
        disabled = formControl?.disabled,
        noResize = false,
        id = defaultId,
        lengthFn = (value) => (value ?? '').length,
        maxLength,
        startAdornmentSlot,
        endAdornmentSlot,
        ...passThroughProps
    } = props;

    // if we have an id, send it back to the form control
    React.useEffect(() => {
        if (!formControl?.hasInputId) {
            formControl?.setInputId?.(id);
        }
    }, [id, formControl]);

    const inputStyles = useInputStyles({
        size,
        variant,
        fullWidth,
        error,
        noResize,
        as,
        startAdornmentSlot,
        endAdornmentSlot,
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const length = lengthFn(typeof event.target.value === 'string' ? event.target.value : '');
        if (typeof formControl?.setCharacterCount === 'function') {
            formControl.setCharacterCount(length);
        }

        if (props.lengthFn) {
            if (formControl) {
                if (length > formControl.characterLimit) {
                    return null;
                }
            } else if (length > maxLength) {
                return null;
            }
        }

        if (!props.disabled && !props.readOnly && onChange) {
            return onChange(event);
        }
        return null;
    };

    // Update character count based on passed through value
    React.useEffect(() => {
        if (typeof formControl?.setCharacterCount !== 'function' || typeof passThroughProps.value !== 'string') {
            return;
        }
        const length = lengthFn(passThroughProps.value);
        formControl.setCharacterCount(length);
    }, [formControl, lengthFn, passThroughProps.value]);

    const baseReturn = (
        <Box
            as={as}
            ref={ref}
            onChange={handleChange}
            aria-label={ariaLabel}
            aria-readonly={props.readOnly}
            aria-invalid={props['aria-invalid']}
            readOnly={props.readOnly}
            disabled={disabled}
            required={formControl?.required || props.required}
            maxLength={props.lengthFn ? undefined : formControl ? formControl.characterLimit : maxLength}
            id={id}
            {...inputStyles}
            {...passThroughProps}
        />
    );

    let actualEndAdornmentSlot = endAdornmentSlot;
    if (error && !actualEndAdornmentSlot) {
        actualEndAdornmentSlot = <IconAlert style={{ color: 'var(--r-alert)' }} />;
    }

    const adornmentEndPadding = passThroughProps?.type === 'search' ? adornmentEndSearch : adornmentEnd;

    return (
        <div
            className={
                startAdornmentSlot || actualEndAdornmentSlot
                    ? cx(
                          adornmentWrapper,
                          !fullWidth && adornmentWrapperInline,
                          startAdornmentSlot ? iconAdornmentStart : '',
                      )
                    : ''
            }
        >
            {startAdornmentSlot && <div className={cx(adornmentContainer, adornmentStart)}>{startAdornmentSlot}</div>}
            {baseReturn}
            {actualEndAdornmentSlot && (
                <div className={cx(adornmentContainer, adornmentEndPadding)}>{actualEndAdornmentSlot}</div>
            )}
        </div>
    );
};

const adornmentContainer = css`
    z-index: 10;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 16px;
    width: 16px;
    pointer-events: none;

    & > svg {
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }
`;

const adornmentWrapper = css`
    position: relative;
`;

const iconAdornmentStart = css`
    input {
        padding-left: 32px;
    }
`;
const adornmentWrapperInline = css`
    display: inline-block;
`;

const adornmentStart = css`
    left: 8px;
`;
const adornmentEnd = css`
    right: 8px;
`;
const adornmentEndSearch = css`
    right: 36px;
`;

export const Input = forwardRef<HTMLElement, InputProps>(InputComponent);

Input.displayName = 'Input';
